import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import importService from "../../services/importService";
import instituteService from "../../services/instituteService";
import { queryKeys } from "./queryKeys";

export const useImports = (filters = {}) => {
  return useQuery({
    queryKey: [...queryKeys.import.all, "list", filters],
    queryFn: () => importService.getImports(filters),
    staleTime: 2 * 60 * 1000,
  });
};

export const useImport = (id) => {
  return useQuery({
    queryKey: [...queryKeys.import.all, "detail", id],
    queryFn: () => importService.getImportById(id),
    enabled: !!id,
    staleTime: 1 * 60 * 1000,
  });
};

export const useImportStatus = (importId) => {
  return useQuery({
    queryKey: [...queryKeys.import.all, "status", importId],
    queryFn: () => importService.getImportStatus(importId),
    enabled: !!importId,
    refetchInterval: (data) => {
      // Refetch every 5 seconds if import is still in progress
      if (data?.status === "processing" || data?.status === "pending") {
        return 5000;
      }
      return false;
    },
    staleTime: 0,
  });
};

export const useImportErrors = (importId) => {
  return useQuery({
    queryKey: [...queryKeys.import.all, "errors", importId],
    queryFn: () => importService.getImportErrors(importId),
    enabled: !!importId,
    staleTime: 2 * 60 * 1000,
  });
};

export const useImportHistory = (params = {}) => {
  return useQuery({
    queryKey: [...queryKeys.import.history(), params],
    queryFn: async () => {
      const res = await importService.getImportHistory(params);
      // return both imports array and pagination metadata
      const payload = res?.data?.data || {};
      const imports = payload.imports || [];
      const pagination = payload.pagination || { total: 0, page: params.page || 1, limit: params.limit || 10, totalPages: 0 };

      const overallFromServer = payload.overallCounts;

      const overallCounts = overallFromServer || {
        totalRows: imports.reduce((s, i) => s + (i.totalRows || 0), 0),
        successCount: imports.reduce((s, i) => s + (i.successCount || i.success || 0), 0),
        failedCount: imports.reduce((s, i) => s + (i.failedCount || i.failed || 0), 0),
      };

      return {
        imports,
        pagination,
        overallCounts,
      };
    },
    placeholderData: keepPreviousData,
    staleTime: 2 * 60 * 1000,
  });
};

export const useImportTemplates = () => {
  return useQuery({
    queryKey: queryKeys.import.templates(),
    queryFn: () => Promise.resolve([]), // This would need to be implemented
    staleTime: 30 * 60 * 1000,
  });
};

export const useUploadBulkData = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ formData, config }) =>
      importService.uploadBulkData(formData, config),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.import.history(),
      });
    },
  });
};

export const useRetryImport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (importId) => importService.retryImport(importId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.import.all, "detail", variables],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.import.all, "status", variables],
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.import.history(),
      });
    },
  });
};

export const useCancelImport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (importId) => importService.cancelImport(importId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.import.all, "detail", variables],
      });
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.import.all, "status", variables],
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.import.history(),
      });
    },
  });
};

export const useDownloadImportFile = () => {
  return useMutation({
    mutationFn: (importId) => importService.downloadImportFile(importId),
  });
};

export const useDownloadTemplate = () => {
  return useMutation({
    mutationFn: (type) => importService.downloadTemplate(type),
  });
};

export const useDownloadErrorReport = () => {
  return useMutation({
    mutationFn: (importId) => importService.downloadErrorReport(importId),
  });
};

export const useExportData = () => {
  return useMutation({
    mutationFn: ({ type, params }) => importService.exportData(type, params),
  });
};

// CSV Import mutations
export const useImportCSVPreview = () => {
  return useMutation({
    mutationFn: ({ entityType, formData }) => {
      if (entityType === 'institutes') return importService.importInstitutesFromCSV(formData, true);
      if (entityType === 'superadmins') return importService.importSuperadminsFromCSV(formData, true);
      if (entityType === 'departments') return importService.importDepartmentsFromCSV(formData, true);
      if (entityType === 'teachers') return importService.importTeachersFromCSV(formData, true);
      if (entityType === 'students') return importService.importStudentsFromCSV(formData, true);
      if (entityType === 'classes') return importService.importClassesFromCSV(formData, true);
      if (entityType === 'courses') return importService.importCoursesFromCSV(formData, true);
      if (entityType === 'subjects') return importService.importSubjectsFromCSV(formData, true);
      throw new Error(`Unsupported entity type: ${entityType}`);
    },
  });
};

export const useImportCSVExecute = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ entityType, formData }) => {
      if (entityType === 'institutes') return importService.importInstitutesFromCSV(formData, false);
      if (entityType === 'superadmins') return importService.importSuperadminsFromCSV(formData, false);
      if (entityType === 'departments') return importService.importDepartmentsFromCSV(formData, false);
      if (entityType === 'teachers') return importService.importTeachersFromCSV(formData, false);
      if (entityType === 'students') return importService.importStudentsFromCSV(formData, false);
      if (entityType === 'classes') return importService.importClassesFromCSV(formData, false);
      if (entityType === 'courses') return importService.importCoursesFromCSV(formData, false);
      if (entityType === 'subjects') return importService.importSubjectsFromCSV(formData, false);
      throw new Error(`Unsupported entity type: ${entityType}`);
    },
    onSuccess: () => {
      // Invalidate relevant queries after successful import
      queryClient.invalidateQueries({ queryKey: queryKeys.import.all });
    },
  });
};

export const useDownloadCSVTemplate = () => {
  const unwrapTemplateJson = (res) => {
    const b = res?.data;
    if (!b || typeof b !== "object" || typeof b.csvData === "string") return res;
    const inner = b.data;
    if (inner && typeof inner === "object" && typeof inner.csvData === "string") {
      return { ...res, data: { ...b, csvData: inner.csvData, fileName: inner.fileName || b.fileName } };
    }
    return res;
  };
  return useMutation({
    mutationFn: async (input = {}) => {
      const entityType = typeof input === "string" ? input : input?.entityType;
      const params = typeof input === "string" ? undefined : input?.params;
      if (entityType === 'institutes') {
        return unwrapTemplateJson(await instituteService.downloadCsvTemplate());
      }
      if (entityType === 'superadmins') {
        return unwrapTemplateJson(await importService.downloadSuperadminTemplate());
      }
      if (entityType === 'departments') {
        return unwrapTemplateJson(await importService.downloadDepartmentTemplate());
      }
      if (entityType === 'teachers') {
        return unwrapTemplateJson(await importService.downloadTeacherTemplate(params));
      }
      if (entityType === 'students') {
        return unwrapTemplateJson(await importService.downloadStudentTemplate(params));
      }
      if (entityType === 'classes' || entityType === 'courses') {
        return unwrapTemplateJson(
          await (entityType === 'courses'
            ? importService.downloadCourseTemplate(params)
            : importService.downloadClassTemplate(params))
        );
      }
      if (entityType === 'subjects') {
        return unwrapTemplateJson(await importService.downloadSubjectTemplate());
      }
      return Promise.reject(new Error(`Template download not supported for entity type: ${entityType}`));
    },
  });
};

